import { Link } from "react-router";
import type { JobSnapshot } from "../../api/types";
import { ProgressTimeline } from "./ProgressTimeline";

interface Props {
  job: JobSnapshot;
}

const STEP_ORDER = [
  "start",
  "research",
  "content",
  "guardian",
  "tts",
  "whisper",
  "broll",
  "render",
];

function prettyCategory(job: JobSnapshot): string {
  const r = job.request;
  let label = r.category;
  if (r.template) label += ` / ${r.template}`;
  else if (r.style) label += ` / ${r.style}`;
  if (r.as_carousel) label += " (carousel)";
  if (r.dry_run) label += " · dry";
  return label;
}

function statusColor(job: JobSnapshot): string {
  if (job.error) return "bg-red-500";
  if (job.completed) return "bg-emerald-500";
  return "bg-brand-blue animate-pulse";
}

function statusLabel(job: JobSnapshot): string {
  if (job.error) return "failed";
  if (job.completed) return "done";
  return "running";
}

/** One job in the live list — collapsed status header + inline timeline. */
export function JobCard({ job }: Props) {
  // Sort steps by canonical pipeline order so the timeline reads naturally.
  const sortedSteps = [...job.steps].sort((a, b) => {
    const aIdx = STEP_ORDER.indexOf(a.name);
    const bIdx = STEP_ORDER.indexOf(b.name);
    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
  });

  // Map to the shape ProgressTimeline expects (uses perf.now-ish numbers,
  // but seconds-since-job-start are fine for display).
  const displaySteps = sortedSteps.map((s) => ({
    name: s.name,
    status: s.status,
    detail: s.detail ?? undefined,
    score: s.score ?? undefined,
    started_at: s.started_at * 1000,
    finished_at: s.finished_at != null ? s.finished_at * 1000 : undefined,
  }));

  const elapsed = Math.round(job.elapsed_s);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const elapsedLabel = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${statusColor(job)}`} />
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-bold text-brand-text-dark">
              {prettyCategory(job)}
            </span>
            <code className="font-mono text-[10px] text-brand-text-dark/40">
              {job.job_id}
            </code>
            <span className="text-[11px] uppercase tracking-wider text-brand-text-dark/50">
              {statusLabel(job)} · {elapsedLabel}
            </span>
          </div>
          {job.request.topic_override ? (
            <div className="mt-1 text-xs text-brand-text-dark/60">
              Topic: {job.request.topic_override}
            </div>
          ) : null}
        </div>
        {job.completed?.post_id ? (
          <Link
            to={`/posts/${job.completed.post_id}`}
            className="rounded-full bg-brand-text-dark px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-text-dark/85"
          >
            Open post →
          </Link>
        ) : null}
      </div>

      {displaySteps.length > 0 ? (
        <div className="mt-4">
          <ProgressTimeline
            steps={displaySteps}
            error={job.error}
            completed={Boolean(job.completed)}
          />
        </div>
      ) : null}
    </div>
  );
}
